import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { RestResourceDataset } from 'app/model/dataset/rest-resource.dataset';
import { DatasetAdapter } from 'app/model/adapters/datasets/dataset.adapter';
import { toParameterObject, toParameterClass } from 'app/model/adapters/linkedservices/parameter-config.util';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';
import { HierarchicalNode } from 'app/model/dataset/hierarchicalnode';
import { parseJsonSchema } from './json.dataset.adapter';
import { ReferenceAdapter } from '../reference.adapter';
import { IHttpConnectionContext } from 'app/interfaces/copilot.pipeline.action.types';
import { isLogicAppExpression, SharedModel } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(RestResourceDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            parameters: '*',
            type: '*',
            folder: '*',
            annotations: '*',
            linkedServiceName: '*',
            connectionProperties: GlobalVariables.disableServerCertificate ? '*' : undefined,
            typeProperties: {
                relativeUrl: '*',
                requestMethod: '*',
                additionalHeaders: '*',
                requestBody: '*',
                paginationRules: '*'
            }
        }
    }
})
export class RestResourceDatasetAdapter extends DatasetAdapter<
    RestResourceDataset,
    Model.DatasetResource,
    IHttpConnectionContext
> {
    public parseHierarchicalSchema(obj: object): HierarchicalNode {
        return parseJsonSchema(obj);
    }
    public override async toFabricCopilotContext(dataset: RestResourceDataset): Promise<IHttpConnectionContext> {
        if (dataset) {
            const dsContext = await super.toFabricCopilotContext(dataset);
            const result: IHttpConnectionContext = {
                ...dsContext,
                Url: ValueAdapter.toResource(dataset, RestResourceDataset.URL, true)
            };
            return Object.keys(result).length > 0 ? result : undefined;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IHttpConnectionContext,
        model?: RestResourceDataset
    ): Promise<RestResourceDataset> {
        if (context) {
            const dataset = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(dataset, RestResourceDataset.URL, context.Url ?? undefined);
            return dataset;
        }
        return model;
    }

    protected async _toResource(dataset: RestResourceDataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = (await super._toResource(dataset)) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.RestDataset;
        modelDataset.type = RestResourceDataset.MODEL.name;

        if (dataset.isV2) {
            modelDataset.typeProperties = {
                relativeUrl: ValueAdapter.toResource(dataset, RestResourceDataset.URL)
            };
            modelDataset.schema = [];
        } else {
            const requestMethod = ValueAdapter.toResource(dataset, RestResourceDataset.METHOD);
            const requestBody =
                typeof requestMethod === 'string' && stringsAreEqualCaseInsensitive(requestMethod, 'GET')
                    ? undefined
                    : ValueAdapter.toResource(dataset, RestResourceDataset.BODY);

            modelDataset.typeProperties = {
                relativeUrl: ValueAdapter.toResource(dataset, RestResourceDataset.URL),
                requestMethod,
                additionalHeaders: toParameterObject(dataset.additionalHeaders),
                requestBody,
                paginationRules: toParameterObject(dataset.paginationRules)
            };
        }

        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(
        modelDatasetResource: Model.DatasetResource,
        dataset?: RestResourceDataset
    ): Promise<RestResourceDataset> {
        if (!dataset) {
            dataset = RestResourceDataset.MODEL.create<RestResourceDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        if (
            this.context.adapterConfig.tolerateInvalidReference &&
            modelDatasetResource?.properties?.linkedServiceName &&
            !isLogicAppExpression(
                (modelDatasetResource.properties.linkedServiceName as SharedModel.LinkedServiceReference).referenceName
            )
        ) {
            const linkedServiceModel = RestResourceDataset.MODEL.attribute('linkedService').constraint.model;
            dataset.linkedService = ReferenceAdapter.toProxyModel(
                linkedServiceModel,
                modelDatasetResource.properties.linkedServiceName,
                this.context
            );
        }
        const model = modelDatasetResource.properties as Model.RestDataset;
        if (model != null) {
            if (model.schema != null) {
                dataset.isV2 = true;
            }
            if (model.typeProperties != null) {
                ValueAdapter.toModel(dataset, RestResourceDataset.URL, model.typeProperties.relativeUrl);
                ValueAdapter.toModel(dataset, RestResourceDataset.METHOD, model.typeProperties.requestMethod);
                dataset.additionalHeaders = toParameterClass(model.typeProperties.additionalHeaders, dataset);
                ValueAdapter.toModel(dataset, RestResourceDataset.BODY, model.typeProperties.requestBody);
                dataset.paginationRules = toParameterClass(model.typeProperties.paginationRules, dataset);
            }
        }

        // TODO: add more properties here
        dataset.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelDatasetResource, RestResourceDataset.MODEL)
        );
        return dataset;
    }
}
